import { useMemo } from "react";
import useUDFilters from "hooks/UD/useUDFilters";
import useUDGroups from "hooks/UD/useUDGroups";
import useUDCompareFilters from "hooks/UD/useUDCompareFilters";
import useUDCompareGroups from "hooks/UD/useUDCompareGroups";

import ExpandableBox from "components/ExpandableBox";
import CheckboxGroup from "components/CheckboxGroup";
import Select from "components/Select";
import Options from "components/Options";

export default function UDResultsSidebar({ params, errors, onChange }) {

   const isCompareMode = params?.compare_mode === "compare";
   
   const { d: filters_items } = useUDFilters( isCompareMode ? null : params.edition );
   const { d: groups } = useUDGroups( isCompareMode ? null : params.edition );
   
   const { d: compareFilters } = useUDCompareFilters( 
      isCompareMode ? params.edition : null, 
      isCompareMode ? params.edition2 : null 
   );
   const { d: compareGroups } = useUDCompareGroups( 
      isCompareMode ? params.edition : null, 
      isCompareMode ? params.edition2 : null 
   );
   
   const displayFilters = isCompareMode ? compareFilters : filters_items;
   const displayGroups = isCompareMode ? compareGroups : groups;
   
   const filters = params.filters || {};

   // W trybie porównania identyfikujemy zmienną "rok badania" po nazwie
   // Zakładamy, że zmienna o nazwie zawierającej "rok" lub "Rok" to zmienna roku badania
   const yearVariableId = useMemo(() => {
      if (!isCompareMode || !displayGroups) return null;
      const yearVar = displayGroups.find(g => 
         g.label && (g.label.toLowerCase().includes("rok") || g.label.toLowerCase().includes("year"))
      );
      return yearVar?.value || null;
   }, [isCompareMode, displayGroups]);

   const groups_with_disabled = useMemo(() => {
      if (!displayGroups) return displayGroups;
      
      const key_variable_id = params.key_variable_id;
      const grouping_variable_ids = params.grouping_variable_ids || [];
      
      return displayGroups.map(group => {
         const isYearVariable = isCompareMode && yearVariableId && String(group.value) === String(yearVariableId);
         const isKeyVariable = key_variable_id ? String(group.value) === String(key_variable_id) : false;
         const isAlreadyGrouping = grouping_variable_ids.some(id => String(id) === String(group.value));
         
         // W trybie porównania: rok jest zawsze aktywny, ale nie można go wybrać ponownie jeśli już jest wybrany
         // Maksymalnie jedna dodatkowa zmienna grupująca poza rokiem
         if (isCompareMode) {
            const hasYearSelected = grouping_variable_ids.some(id => String(id) === String(yearVariableId));
            const hasAdditionalGrouping = grouping_variable_ids.length > (hasYearSelected ? 1 : 0);
            
            return {
               ...group,
               disabled: isKeyVariable || (hasAdditionalGrouping && !isYearVariable && !isAlreadyGrouping)
            };
         }
         
         return {
            ...group,
            disabled: isKeyVariable
         };
      });
   }, [displayGroups, params.key_variable_id, params.grouping_variable_ids, isCompareMode, yearVariableId]);
   
   return displayGroups && displayFilters ? (
      <>
         <div className="ud-results-sidebar-filters box">

            <ExpandableBox
               extra_classes="mini"
               heading={ <strong style={{ display: "block", textAlign: "right"}}> Zwiń wszystko </strong> }
               style={{ border: 0, padding: 0 }}
            >

            { displayFilters.map((({ id, name: heading, variable_id, ...rest }, i ) => {

               const options = (rest.options || []).filter( opt => opt.label !== " " );
               const multiple = (rest.options || []).length > 2;

               const onInputChange = ( n, v ) => {
                  filters[ n ] = v;
                  onChange( "filters", filters );
               };

               const disabled = params.grouping_variable_ids?.includes( variable_id );

               return (
                  <ExpandableBox 
                     key={ id } 
                     heading={ heading }
                     style={ i + 1 === displayFilters.length ? { border: 0, paddingBottom: 0 } : undefined }
                     extra_classes="mini"
                     default_open={false}
                  >
                     { multiple ? 
                        <CheckboxGroup 
                           name={ variable_id }
                           value={ filters[ variable_id ] || []}
                           buttons={ options }
                           disabled={ disabled }
                           onChange={ onInputChange }
                        /> : (
                           <Select
                              name={ variable_id }
                              value={ filters[ variable_id ]}
                              options={ options }
                              disabled={ disabled }
                              onChange={ onInputChange }   
                           />
                        ) 
                     }
                  </ExpandableBox>
               )
            }))}
            </ExpandableBox>
         </div>

         <div className="box">
            <strong> Grupowanie </strong> <br /> <br />
            {isCompareMode && (
               <div className="compare-mode-info" style={{ marginBottom: "10px", padding: "8px", borderRadius: "4px", fontSize: "0.9em" }}>
                  <strong>Tryb porównania:</strong> Rok badania jest zawsze aktywną zmienną porównawczą. Możesz wybrać maksymalnie jedną dodatkową zmienną grupującą.
               </div>
            )}

             <Options
               multiple
               name="grouping_variable_ids"
               value={ params.grouping_variable_ids || []}
               error={ errors.grouping_variable_ids }
               max={ isCompareMode ? 1 : 2 }
               label={isCompareMode 
                  ? "Wybierz maksymalnie jedną dodatkową grupę (rok badania jest zawsze aktywny)"
                  : "Wybierz maksymalnie dwie grupy, które wprowadzą podział prezentacji wyników"}
               extra_classes="mini"
               options={ groups_with_disabled }
               onChange={( n, v ) => {
                  // W trybie porównania: rok jest zawsze aktywny, więc dodajemy go automatycznie jeśli nie jest już w liście
                  let finalV = [...v];
                  if (isCompareMode && yearVariableId) {
                     const hasYear = finalV.some(id => String(id) === String(yearVariableId));
                     if (!hasYear) {
                        finalV = [yearVariableId, ...finalV].slice(0, 2); // Maksymalnie 2 (rok + jedna dodatkowa)
                     }
                  }
                  
                  // Jeśli wybrano zmienną grupującą, która jest już wybrana jako zmienna kluczowa, usuń ją z kluczowej
                  const key_variable_id = params.key_variable_id;
                  const new_key_variable_id = (key_variable_id && finalV.some(id => String(id) === String(key_variable_id))) 
                     ? undefined 
                     : key_variable_id;

                  const f = {...filters };
                  const v1 = finalV[0];
                  const v2 = finalV[1];

                  if ( v1 ) f[v1] = undefined;
                  if ( v2 ) f[v2] = undefined;

                  const changes = {
                     [n]: finalV,
                     filters: f
                  };
                  
                  if (new_key_variable_id !== key_variable_id) {
                     changes.key_variable_id = new_key_variable_id;
                  }

                  onChange(changes);
               }}
            />
         </div>
      </>
   ) : null
}